import { StateCreator } from 'zustand';
import { Middleware } from './common';
import { DashboardStoreState } from './DashboardProvider';
import {
  addPanelGroup,
  createEmptyPanelGroup,
  PanelGroupDefinition,
  PanelGroupId,
  PanelGroupSlice,
} from './panel-group-slice';

export interface PanelGroupEditor {
  mode: 'Add' | 'Edit';
  kind: PanelGroupDefinition['kind'];
  initialValues: PanelGroupEditorValues;
  applyChanges: (next: PanelGroupEditorValues) => void;
  close: () => void;
}

export interface PanelGroupEditorValues {
  title: string;
  isCollapsed: boolean;
  splitEnabled: boolean;
  splitVariableName?: string;
}

/**
 * Slice that handles the visual editor state and related actions for adding or editing Panel Groups.
 */
export interface PanelGroupEditorSlice {
  /**
   * State that's present when the panel group editor is open.
   */
  panelGroupEditor?: PanelGroupEditor;

  /**
   * Opens the panel group editor to add a new panel group.
   */
  openAddPanelGroup: () => void;

  /**
   * Opens the panel group editor to edit an existing panel group.
   */
  openEditPanelGroup: (panelGroupId: PanelGroupId) => void;

  /**
   * State of quick start drawer.
   */
  isQuickStartDrawerOpen: boolean;

  /**
   * Opens the quick start drawer.
   */
  toggleQuickStartDrawer: () => void;
}

export const createPanelGroupEditorSlice: StateCreator<
  // Actions in here need to modify Panel Group state
  PanelGroupEditorSlice & PanelGroupSlice & DashboardStoreState,
  Middleware,
  [],
  PanelGroupEditorSlice
> = (set, get) => ({
  panelGroupEditor: undefined,

  openAddPanelGroup: () => {
    // Create the editor state
    const editor: PanelGroupEditor = {
      mode: 'Add',
      kind: 'Grid',
      initialValues: {
        title: '',
        isCollapsed: false,
        splitEnabled: false,
        splitVariableName: undefined,
      },
      applyChanges(next) {
        const newGroup = createEmptyPanelGroup();
        newGroup.title = next.title;
        newGroup.isCollapsed = next.isCollapsed;
        set((draft) => {
          addPanelGroup(draft, newGroup);
        });
      },
      close() {
        set((draft) => {
          draft.panelGroupEditor = undefined;
        });
      },
    };

    // Open the editor
    set(
      (draft) => {
        draft.panelGroupEditor = editor;
        // Make sure we are in edit mode
        draft.isEditMode = true;
      },
      false,
      '[Panel Groups] openAddPanelGroup'
    );
  },

  openEditPanelGroup: (panelGroupId) => {
    const existingGroup = get().panelGroups[panelGroupId];
    if (existingGroup === undefined) {
      throw new Error(`Panel group with Id ${panelGroupId} does not exist`);
    }

    // Create the editor state
    const editor: PanelGroupEditor = {
      mode: 'Edit',
      kind: existingGroup.kind,
      initialValues: {
        title: existingGroup.title ?? '',
        isCollapsed: existingGroup.isCollapsed,
        splitEnabled: (existingGroup.kind === 'Grid' && existingGroup.splitEnabled) ?? false,
        splitVariableName: existingGroup.splitVariableName,
      },
      applyChanges(next) {
        set((draft) => {
          const group = draft.panelGroups[panelGroupId];
          if (group === undefined) {
            throw new Error(`Panel group with Id ${panelGroupId} does not exist`);
          }
          group.title = next.title;
          group.isCollapsed = next.isCollapsed;
          if (group.kind === 'Grid') {
            group.splitEnabled = next.splitEnabled;
          }
          group.splitVariableName = next.splitVariableName;
        });
      },
      close() {
        set((draft) => {
          draft.panelGroupEditor = undefined;
        });
      },
    };

    // Open the editor
    set(
      (draft) => {
        draft.panelGroupEditor = editor;
      },
      false,
      '[Panel Groups] openEditPanelGroup'
    );
  },

  isQuickStartDrawerOpen: false,

  toggleQuickStartDrawer: () => {
    set((draft) => {
      draft.isQuickStartDrawerOpen = !draft.isQuickStartDrawerOpen;

      // Make sure we are in edit mode
      draft.isEditMode = true;
    });
  },
});
